/**
 * Canvas replay: scrubbing the room's Liveblocks version history. While
 * `replaying`, `CanvasContext` renders `frame` in place of live storage and
 * takes the read-only path, and `useCanvasPermissions` reports no edit or
 * comment rights, so every surface goes read-only at once.
 *
 * The frame sits in its own context so a scrub re-renders the document
 * pipeline, not every consumer of `replaying`.
 */

import type { Edge, Node } from "@xyflow/react";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface CanvasReplayFrame {
  nodes: Node[];
  edges: Edge[];
}

interface CanvasReplayContextType {
  replaying: boolean;
  start: () => void;
  stop: () => void;
  setFrame: (frame: CanvasReplayFrame | null) => void;
  /** Camera follows each frame's changes. Off once the viewer pans or zooms. */
  follow: boolean;
  setFollow: (follow: boolean) => void;
}

const CanvasReplayContext = createContext<CanvasReplayContextType | null>(null);
const CanvasReplayFrameContext = createContext<CanvasReplayFrame | null>(null);

export function CanvasReplayProvider({ children }: { children: ReactNode }) {
  const [replaying, setReplaying] = useState(false);
  const [frame, setFrame] = useState<CanvasReplayFrame | null>(null);
  const [follow, setFollow] = useState(true);

  const value = useMemo<CanvasReplayContextType>(
    () => ({
      replaying,
      start: () => {
        setReplaying(true);
        setFollow(true);
      },
      stop: () => {
        setReplaying(false);
        setFrame(null);
      },
      setFrame,
      follow,
      setFollow,
    }),
    [replaying, follow],
  );

  return (
    <CanvasReplayContext.Provider value={value}>
      <CanvasReplayFrameContext.Provider value={frame}>
        {children}
      </CanvasReplayFrameContext.Provider>
    </CanvasReplayContext.Provider>
  );
}

/** Null outside a canvas route (the app shell's explorer, tests). */
export function useCanvasReplay(): CanvasReplayContextType | null {
  return useContext(CanvasReplayContext);
}

export function useCanvasReplayFrame(): CanvasReplayFrame | null {
  return useContext(CanvasReplayFrameContext);
}
